import get from 'lodash.get';
import { emptyArray } from '../../default-props/empty';
import UserPermissionService from '../../services/user-permission-service';
import UserSettings, { quickshipUserSettings } from '../../enums/user-settings/user-settings';
import DateService from '../../services/date-service';

export const selectCurrentUserInfo = (state) => get(state, 'currentUser');
export const selectCurrentUserId = (state) => get(selectCurrentUserInfo(state), 'userId');
export const selectIsAccountOwner = (state) => get(selectCurrentUserInfo(state), 'isAccountOwner');
export const hasOrderManagementRole = (state) =>
  UserPermissionService.userHasOrderManagementRole(get(state, 'currentUser.roles', emptyArray));
export const hasShippingRole = (state) => UserPermissionService.userHasShippingRole(get(state, 'currentUser.roles', emptyArray));
export const hasCustomerManagementRole = (state) =>
  UserPermissionService.userHasCustomerManagementRole(get(state, 'currentUser.roles', emptyArray));
export const hasProductManagementRole = (state) =>
  UserPermissionService.userHasProductManagementRole(get(state, 'currentUser.roles', emptyArray));
export const hasPurchasingRole = (state) => UserPermissionService.userHasPurchasingRole(get(state, 'currentUser.roles', emptyArray));
export const isSysAdmin = (state) => UserPermissionService.userIsSystemAdministrator(get(state, 'currentUser.roles', emptyArray));
export const hasAdministrationRole = (state) =>
  UserPermissionService.userHasAdministrationRole(get(state, 'currentUser.roles', emptyArray));
export const hasInventoryManagementRole = (state) =>
  UserPermissionService.userHasInventoryManagementRole(get(state, 'currentUser.roles', emptyArray));
export const selectUserSettings = (state) => get(state, 'userSettings', emptyArray);
export const userHasOrderViewRestriction = (state) =>
  UserPermissionService.userHasOrderViewRestriction(get(state, 'currentUser.restrictions', emptyArray));
export const userHasShipmentViewRestriction = (state) =>
  UserPermissionService.userHasShipmentViewRestriction(get(state, 'currentUser.restrictions', emptyArray));
export const userRoles = (state) => get(state, 'currentUser.roles', emptyArray);

export const selectUserSetting = (state, { userSetting }) => {
  const userSettings = get(state, 'userSettings', emptyArray);
  const matchingSetting = userSettings.find((setting) => setting.settingName === userSetting);

  if (!matchingSetting) {
    return;
  }

  return matchingSetting.settingValue;
};

export const selectUserDateFormat = (state) => {
  const userSettings = get(state, 'userSettings', emptyArray);
  const dateFormatSetting = userSettings.find((setting) => setting.settingName === DateService.getUserDateFormatSettingName());

  if (!dateFormatSetting) {
    return DateService.getDefaultDateFormat();
  }

  return dateFormatSetting.settingValue;
};

export const selectUserTimeFormat = (state) => {
  const userSettings = get(state, 'userSettings', emptyArray);
  const timeFormatSetting = userSettings.find((setting) => setting.settingName === DateService.getUserTimeFormatSettingName());

  if (!timeFormatSetting) {
    return DateService.getDefaultTimeFormat();
  }

  return timeFormatSetting.settingValue;
};

export const selectUserSettingsReady = (state) => {
  return state?.userSettingsReady;
};

export const selectUserQuickshipSettings = (state) => {
  const userSettings = selectUserSettings(state);
  return userSettings
    .filter((setting) => quickshipUserSettings.findIndex((qsSetting) => qsSetting.settingName === setting.settingName) > -1)
    .map((setting) => {
      const newSetting = { ...setting };
      if (setting.settingValue.toString().toLowerCase() === 'true') {
        newSetting.settingValue = true;
      }
      if (setting.settingValue.toString().toLowerCase() === 'false') {
        newSetting.settingValue = false;
      }
      return newSetting;
    });
};

const selectHasQuickShipRestriction = (state) =>
  UserPermissionService.userHasQuickshipRestriction(get(state, 'currentUser.restrictions', emptyArray));
export const selectIsQuickshipEnabledForUser = (state) => {
  const isEnabled = selectUserSetting(state, {
    userSetting: UserSettings.isQuickshipEnabled.settingName,
  });
  const isEnabledActual = isEnabled?.toString().toLowerCase() === 'true';
  return !selectHasQuickShipRestriction(state) && isEnabledActual;
};

export const selectQuickshipShouldShowErrorsOrWarnings = (state) => {
  const enabled = selectIsQuickshipEnabledForUser(state);
  const userQuickshipSettings = selectUserQuickshipSettings(state) || [];
  const showErrorsOrWarningsSettings = userQuickshipSettings.filter(
    (setting) =>
      setting.settingName === UserSettings.quickshipShowErrors.settingName ||
      setting.settingName === UserSettings.quickshipShowWarnings.settingName,
  );
  const showErrorsOrWarnings =
    !enabled || (showErrorsOrWarningsSettings.length > 0 && showErrorsOrWarningsSettings.some(({ settingValue }) => settingValue === true));
  return {
    showErrorsOrWarnings,
    showWarnings:
      showErrorsOrWarningsSettings.find((setting) => setting.settingName === UserSettings.quickshipShowWarnings.settingName)
        ?.settingValue ?? false,
    showErrors:
      showErrorsOrWarningsSettings.find((setting) => setting.settingName === UserSettings.quickshipShowErrors.settingName)?.settingValue ??
      false,
  };
};

export const selectQuickshipShouldShowPrintDialog = (state) => {
  const userQuickshipSettings = selectUserQuickshipSettings(state) || [];
  return (
    userQuickshipSettings.find((setting) => setting.settingName === UserSettings.quickshipShowPrintDialogPreview.settingName)
      ?.settingValue ?? false
  );
};
